import { useTranslation } from 'module/hooks/use-translation';
import { logger } from 'module/logger';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { ActionBar } from 'module/ui/components/molecules/action-bar';
import { HeaderWithIconTitle } from 'module/ui/components/molecules/page/page';
import { Argon as ArgonTemplate } from 'module/ui/components/templates/argon';
import { Content } from 'module/ui/components/templates/argon/components';
import { faEnvelopeOpen } from '@fortawesome/pro-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { EmailSettings } from 'api/email';
import { EmailIntelligenceEnabledTo } from 'api/email';
import {
  useGetEmailSettings,
  useUpdateEmailSettings,
  useCreateEmailSettings,
} from 'queries/emails';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { InitialPage } from './initial-page';
import { EmailDuration, Settings } from './settings';

const Argon = styled(ArgonTemplate)`
  ${Content} {
    border: none;
  }
  ${ActionBar} {
    box-shadow: none;
  }
`;

export const EmailIntelligencePage = ({
  isModuleFed = true,
}: {
  isModuleFed: boolean;
}) => {
  const title = useTranslation('email_settings');
  const { data: emailFetchedSettings, isLoading: isEmailSettingsLoading } =
    useGetEmailSettings();
  const queryClient = useQueryClient();
  const updateEmailSettingsMutation = useUpdateEmailSettings();
  const createEmailSettingsMutation = useCreateEmailSettings();

  const handleEmailSettingsUpdate = async (settings: EmailSettings) => {
    try {
      if (settings?.ftueCompleted) {
        await updateEmailSettingsMutation.mutateAsync(settings);
      } else {
        await createEmailSettingsMutation.mutateAsync(settings);
      }
      await queryClient.invalidateQueries({ queryKey: ['members'] });
    } catch (error) {
      logger.error('response failed', { error });
    }
  };

  return (
    <Argon
      documentTitle={title}
      title={
        <HeaderWithIconTitle
          icon={<FontAwesomeIcon icon={faEnvelopeOpen} size={20} />}
          title={title}
        />
      }
      description={
        <FormattedMessage id="settings.email_settings.description" />
      }
      actions={{}}
      content={
        isEmailSettingsLoading ? (
          <PageLoading />
        ) : !emailFetchedSettings?.ftueCompleted ? (
          <InitialPage onToggle={handleEmailSettingsUpdate} />
        ) : (
          <Settings
            isModuleFed={isModuleFed}
            emailSettings={
              emailFetchedSettings ?? {
                emailIntelligenceEnabled: true,
                backSyncStartRange: EmailDuration.Past90Days,
                backSyncStartDate: '',
                ftueCompleted: true,
                emailIntelligenceEnabledTo:
                  EmailIntelligenceEnabledTo.SpecificTeamMembersOnly,
              }
            }
            onToggle={handleEmailSettingsUpdate}
          />
        )
      }
    ></Argon>
  );
};
